import { PERSONA_PRESETS, useTalkBarPersona } from '../../hooks/usePersonaPresets';

/**
 * V6.1.A Voice GM (2026-06-02) — inline persona picker in the TalkBar.
 *
 * Compact <select> chip that lets the player swap GM voice mid-session
 * without opening the Audio drawer. Reuses useNarratorVoice as the
 * source of truth so the Audio drawer's VoicePickerSection + every TTS
 * consumer (ReadAloudControl, useTtsQueue, useGMDispatcher) stay in
 * sync via the existing custom event.
 *
 * When the persisted voice is NOT one of the 5 presets (e.g. user picked
 * `bf_lily` from the full Audio-drawer catalog), the picker shows a
 * separate "Custom" option so it doesn't silently flip the selection
 * to one of the presets just by being mounted.
 */

export interface TalkBarPersonaPickerProps {
  testidPrefix?: string;
}

export function TalkBarPersonaPicker({
  testidPrefix = 'br-talk-bar-persona',
}: TalkBarPersonaPickerProps) {
  const { currentPersonaId, setPersona, currentVoiceId } = useTalkBarPersona();
  const isCustom = currentPersonaId === 'custom';

  return (
    <label
      className="br-talk-bar-persona br-label"
      data-testid={testidPrefix}
      data-persona={currentPersonaId}
      title={`GM voice: ${currentVoiceId}`}
    >
      <span className="br-talk-bar-persona-label">GM</span>
      <select
        className="br-talk-bar-persona-select"
        data-testid={`${testidPrefix}-select`}
        value={isCustom ? 'custom' : currentPersonaId}
        onChange={(e) => {
          const next = e.target.value;
          if (next !== 'custom') {
            setPersona(next);
          }
        }}
      >
        {isCustom && (
          <option value="custom" data-testid={`${testidPrefix}-option-custom`}>
            Custom ({currentVoiceId})
          </option>
        )}
        {PERSONA_PRESETS.map((p) => (
          <option
            key={p.id}
            value={p.id}
            data-testid={`${testidPrefix}-option-${p.id}`}
          >
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default TalkBarPersonaPicker;
